import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePatientChartBundle, chartImagingFilters } from '@/hooks/usePatientChartBundle';
import { usePatientMedicalRecords } from '@/hooks/useMedicalRecords';
import { useAttachments } from '@/hooks/useAttachments';
import { useImagingOrders } from '@/hooks/useImagingOrders';
import { usePatient } from '@/hooks/usePatients';
import { usePatientEpisodes } from '@/hooks/useEpisodes';
import type { Episode } from '@/services/api/episodesApi';
import apiClient from '@/services/apiClient';
import { medicalHistoryApi } from '@/services/api/medicalHistoryApi';
import { prescriptionApi } from '@/services/prescriptionApi';
import { clinicLabOrdersApi } from '@/services/api/clinicLabOrdersApi';
import { referralsApi } from '@/services/api/referralsApi';
import { splitAllergyText, allergenOf, isSevereAllergy, isNoAllergyText } from '@/utils/allergyText';
import type { MedicalRecord } from '@/services/api/medicalRecordsApi';
import type { ClinicLabOrder } from '@/services/api/clinicLabOrdersApi';
import type { ImagingOrder } from '@/services/imagingOrderApi';
import type { Prescription } from '@/services/prescriptionApi';
import { isCurrentlyTaking } from '@/utils/prescriptionStatus';
import type { Attachment } from '@/services/api/attachmentsApi';
import type { Referral } from '@/services/api/referralsApi';
import type { MedicalHistory } from '@/services/api/medicalHistoryApi';

/**
 * THE FILE IS ONE SURFACE — the data behind f4 · f5 · f5b · f5c · f6 · f6b · f7.
 *
 * One request, not ten: `/patients/:id/chart` returns the patient, the visits,
 * the structured history, the vitals series, the prescriptions, both order
 * kinds, the referrals, the attachments and the invoices in a single round
 * trip, each section permission-gated server-side.
 *
 * `null` ON A SECTION IS NOT `[]`. The bundle uses `null` for "you may not read
 * this" and `[]` for "there are none", and the two must reach the screen as
 * different words: a hidden medication list and an empty one look identical and
 * mean opposite things. Every section here therefore carries its own
 * `permitted` flag rather than being flattened to an array.
 *
 * WHAT IS DERIVED AND WHAT IS STORED — stated because the canvas shows several
 * things the backend does not store, and quietly inventing them is the failure
 * mode this file exists to avoid:
 *
 *   stored    visits, SOAP, diagnoses, chronic diseases (ICD + onset + status),
 *             surgeries, immunizations, family history, vitals, prescriptions,
 *             lab and imaging orders, attachments, referrals
 *   derived   the 3-line summary (assembled from the parts above), the pinned
 *             problem (the active chronic disease today's visits are coded to),
 *             the visit count on it, the PMH prose
 *   absent    episodes and their "why open", a follow-up due date, who took a
 *             vital — see docs/BACKEND-BACKLOG.md
 */

export interface SafetyAllergy { label: string; full: string; severe: boolean }

/** The server's verdict against the reviewed band, or absent where none applies. */
export type VitalFlag = 'normal' | 'abnormal' | 'critical';

export interface VitalReading {
  value: string;
  at: string;
  /** Who took it — the design's "the nurse's name is part of the value". */
  by: string | null;
  /**
   * ABNORMALS SPEAK, NORMALS STAY QUIET — and only the server may decide
   * which is which. A threshold table kept in the browser would drift out of
   * step with the lab the moment either changed, and the number a doctor acts
   * on must not depend on how stale their tab is.
   *
   * Undefined means "no reviewed band for this", which is NOT "normal": the
   * value is shown as recorded, in the same quiet type, with nothing claimed
   * about it.
   */
  flag?: VitalFlag;
}
export interface Measure {
  key: string;
  label: string;
  latest: string;
  readings: VitalReading[];
  first?: VitalReading;
}

export interface MedGroup {
  id: string;
  title: string;
  drugs: Prescription[];
  date?: string;
  active: boolean;
}

/** A section the caller may not read reaches the screen as a refusal, not a zero. */
export interface Section<T> { items: T[]; permitted: boolean }

const sec = <T,>(v: T[] | null | undefined): Section<T> =>
  v == null ? { items: [], permitted: false } : { items: v, permitted: true };

const dateOf = (v: string | Date | null | undefined): number =>
  v ? new Date(v).getTime() : 0;

const year = (v: string | null | undefined): string =>
  v ? String(new Date(v).getFullYear()) : '—';

/** Same calendar day as now, in the reader's own timezone. */
export function isToday(v: string | Date | null | undefined): boolean {
  if (!v) return false;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return false;
  return d.toDateString() === new Date().toDateString();
}

export function shortDate(v: string | Date | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'today';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** `follow_up` → `follow-up`. The type is the only thing that names a visit. */
const visitKind = (t?: string) => (t ?? 'visit').replace(/_/g, ' ');

/** A 403 is the server saying "not yours to read" — never "there are none". */
const forbidden = (e: unknown): boolean =>
  (e as { response?: { status?: number } })?.response?.status === 403;

export function useDoctorPatientFile(patientId: string) {
  const navigate = useNavigate();

  /**
   * ONE REQUEST, NINE READERS. The bundle hook does not return the chart — it
   * SEEDS the per-section query keys the rest of the app already uses, and the
   * queries below read those seeded entries instead of fetching. That is what
   * keeps this page live: writing a prescription from the file invalidates
   * `['prescriptions', 'byPatient', id]` and this screen refreshes, which a
   * private copy of the bundle would not do.
   */
  const bundle = usePatientChartBundle(patientId);
  const seeding = bundle.isLoading;

  const patientQ = usePatient(patientId);
  const visitsQ = usePatientMedicalRecords(patientId, !seeding);
  const historyQ = useQuery({
    queryKey: ['medical-history', patientId],
    queryFn: () => medicalHistoryApi.getByPatientId(patientId),
    enabled: !!patientId && !seeding,
    staleTime: 60_000,
    retry: false,
  });
  const vitalsQ = useQuery({
    queryKey: ['vitals-history', patientId, 90],
    queryFn: async () => {
      const r = await apiClient.get(`/medical-records/patients/${patientId}/vitals-history?days=90`);
      return Array.isArray(r.data.data) ? (r.data.data as Array<Record<string, unknown>>) : [];
    },
    enabled: !!patientId && !seeding,
    staleTime: 60_000,
    retry: false,
  });
  const rxQ = useQuery({
    queryKey: ['prescriptions', 'byPatient', patientId],
    queryFn: () => prescriptionApi.getByPatient(patientId),
    enabled: !!patientId && !seeding,
    staleTime: 30_000,
    retry: false,
  });
  const labsQ = useQuery({
    queryKey: ['clinic-lab-orders', 'by-patient', patientId],
    queryFn: () => clinicLabOrdersApi.getByPatient(patientId),
    enabled: !!patientId && !seeding,
    staleTime: 30_000,
    retry: false,
  });
  const referralsQ = useQuery({
    queryKey: ['referrals', 'by-patient', patientId],
    queryFn: () => referralsApi.getByPatient(patientId),
    enabled: !!patientId && !seeding,
    staleTime: 30_000,
    retry: false,
  });
  const imagingQ = useImagingOrders(chartImagingFilters(patientId));
  const episodesQ = usePatientEpisodes(patientId);
  const attachmentsQ = useAttachments(patientId);

  const isLoading = seeding || patientQ.isLoading || visitsQ.isLoading;
  const isError = patientQ.isError;

  const patient = patientQ.data?.data as unknown as {
    id: string; firstName?: string; lastName?: string; dateOfBirth?: string;
    gender?: string; phone?: string; medicalRecordNumber?: string;
    allergies?: string; medicalHistory?: string[]; notes?: string;
    bloodType?: string; socialHistory?: { smokingStatus?: string };
    pregnancyStatus?: string | null; pregnancyDueDate?: string | null;
  } | undefined;

  const history = (historyQ.data ?? null) as MedicalHistory | null;

  const name = useMemo(
    () => [patient?.firstName, patient?.lastName].filter(Boolean).join(' ').trim(),
    [patient?.firstName, patient?.lastName],
  );

  const ageSex = useMemo(() => {
    const bits: string[] = [];
    if (patient?.dateOfBirth) {
      const y = Math.floor((Date.now() - new Date(patient.dateOfBirth).getTime()) / 31_557_600_000);
      if (Number.isFinite(y) && y >= 0) bits.push(String(y));
    }
    if (patient?.gender === 'male') bits.push('M');
    else if (patient?.gender === 'female') bits.push('F');
    return bits.join(' ');
  }, [patient?.dateOfBirth, patient?.gender]);

  /**
   * SAFETY IS THE HEADER, and it takes BOTH sources. The structured
   * `medical_history.allergies` carries allergen, reaction and severity as
   * fields; the patient row carries whatever reception typed. Neither is
   * authoritative on its own — a clinic that never filled the structured
   * record still has the free-text one, and vice versa — so both are read and
   * merged on the allergen name. A duplicate is a cosmetic defect; a dropped
   * allergen is a safety one.
   */
  const allergies = useMemo<SafetyAllergy[]>(() => {
    const out = new Map<string, SafetyAllergy>();
    for (const a of history?.allergies ?? []) {
      if (a.isActive === false) continue;
      const full = [a.allergen, [a.reaction, a.severity].filter(Boolean).join(', ')]
        .filter(Boolean)
        .join(' (') + (a.reaction || a.severity ? ')' : '');
      out.set(a.allergen.toLowerCase(), {
        label: a.allergen,
        full,
        severe: a.severity === 'severe' || a.severity === 'life-threatening',
      });
    }
    for (const entry of splitAllergyText(patient?.allergies)) {
      const label = allergenOf(entry) || entry;
      const key = label.toLowerCase();
      if (out.has(key)) continue;
      out.set(key, { label, full: entry, severe: isSevereAllergy(entry) });
    }
    return [...out.values()];
  }, [history?.allergies, patient?.allergies]);

  /**
   * "No known drug allergies" is a STATEMENT someone made, and it is not the
   * same as an empty field nobody filled. The chip has to be able to say which.
   */
  const allergiesDeclaredNone = useMemo(
    () => allergies.length === 0 && !!patient?.allergies?.trim() && isNoAllergyText(patient.allergies),
    [allergies.length, patient?.allergies],
  );

  const conditions = useMemo(() => {
    const out = new Map<string, { name: string; icd?: string; status: string; since?: string }>();
    for (const c of history?.chronicDiseases ?? []) {
      if (c.status === 'resolved') continue;
      out.set(c.name.toLowerCase(), { name: c.name, icd: c.icdCode, status: c.status, since: c.onsetDate });
    }
    for (const h of patient?.medicalHistory ?? []) {
      if (!h || out.has(h.toLowerCase())) continue;
      out.set(h.toLowerCase(), { name: h, status: 'active' });
    }
    return [...out.values()];
  }, [history?.chronicDiseases, patient?.medicalHistory]);

  const visits = useMemo(() => {
    const rows = (visitsQ.records ?? []) as MedicalRecord[];
    return [...rows].sort((a, b) => dateOf(b.visitDate) - dateOf(a.visitDate));
  }, [visitsQ.records]);

  /* Visits are gated on the same `medical_records:read` the file itself needs,
     so there is no "permitted but hidden" state to represent here — a caller
     without it never reaches this page. What IS worth carrying is the page
     count, so the table can say 25 of 187 rather than imply 25 is all of it. */
  const visitsPermitted = true;
  const openDraft = useMemo(() => visits.find((v) => v.status === 'draft') ?? null, [visits]);

  /* "PANELS NEWEST FIRST" IS A PROMISE THE CAPTION MAKES, so the order is the
     screen's, not whatever the endpoint happened to return: a panel is dated
     by the day its RESULT came back, and a result entered today belongs at the
     top even when the sample was taken a fortnight ago. Same for a study and
     for a document. */
  const newestFirst = <T,>(rows: T[], at: (x: T) => string | null | undefined): T[] =>
    [...rows].sort((a, b) => dateOf(at(b)) - dateOf(at(a)));

  const labs = useMemo(
    () => sec(forbidden(labsQ.error)
      ? null
      : newestFirst((labsQ.data ?? []) as ClinicLabOrder[], (l) => l.resultDate ?? l.orderDate)),
    [labsQ.data, labsQ.error],
  );
  const imaging = useMemo(
    () => sec(forbidden(imagingQ.error)
      ? null
      : newestFirst((imagingQ.data?.data ?? []) as ImagingOrder[], (i) => i.completedDate ?? i.orderDate)),
    [imagingQ.data, imagingQ.error],
  );
  const attachments = useMemo(
    () => sec(forbidden(attachmentsQ.error)
      ? null
      : newestFirst((attachmentsQ.data ?? []) as Attachment[], (a) => a.createdAt)),
    [attachmentsQ.data, attachmentsQ.error],
  );
  const referrals = useMemo(
    () => sec(forbidden(referralsQ.error) ? null : ((referralsQ.data ?? []) as Referral[])),
    [referralsQ.data, referralsQ.error],
  );

  /**
   * THE PINNED EPISODE — now a real one.
   *
   * This used to derive a stand-in from the chronic-disease list and the
   * visits' diagnosis codes, and it had to admit on screen that "why it is
   * still open is not recorded". There is an episode model now, so the card
   * reads the thread itself — including the one field the whole model exists
   * for, the reason it is open.
   *
   * The newest thread that is still running is the pinned one; a chart with
   * two open threads shows the most recent and the rest are a click away. A
   * closed thread is never pinned — it is history, not what you are in.
   */
  const episodes = useMemo(() => (episodesQ.data ?? []) as Episode[], [episodesQ.data]);
  const openEpisodes = useMemo(
    () => episodes.filter((e) => e.state !== 'closed'),
    [episodes],
  );
  const episode = openEpisodes[0] ?? null;

  /**
   * THE 3-LINE STORY, assembled. The canvas shows a written summary; no field
   * holds one. Every clause below is read from the chart, so the card is true —
   * and it is labelled as assembled, because a doctor reading a summary needs
   * to know whether a person wrote it.
   */
  const summary = useMemo(() => {
    const lastSigned = visits.find((v) => v.status === 'signed');
    const bits: string[] = [];
    const active = conditions.filter((c) => c.status === 'active').map((c) => c.name);
    /* THREE LINES, NO ECHO. The condition, the visit's complaint and its
       assessment are often the same words three times ("Other seasonal
       allergic rhinitis. Last seen for review of other seasonal allergic
       rhinitis. Seasonal allergic rhinitis…"); a clause whose words are
       mostly said already is dropped. */
    const words = (x: string) => x.toLowerCase().match(/[a-z\u0600-\u06ff]{3,}/g) ?? [];
    const said = new Set<string>();
    const novel = (x: string) => {
      const w = words(x);
      const fresh = w.filter((t) => !said.has(t)).length;
      return !w.length || fresh / w.length > 0.4;
    };
    const push = (x: string) => { bits.push(x); words(x).forEach((t) => said.add(t)); };
    if (active.length) push(`${active.slice(0, 3).join(', ')}${active.length > 3 ? ` +${active.length - 3}` : ''}.`);
    if (lastSigned) {
      const why = (lastSigned.chiefComplaint ?? visitKind(lastSigned.type)).toLowerCase();
      push(novel(why) ? `Last seen ${shortDate(lastSigned.visitDate)} for ${why}.` : `Last seen ${shortDate(lastSigned.visitDate)}.`);
      const a = (lastSigned.assessment ?? '').split(/(?<=\.)\s/)[0];
      if (a && novel(a)) push(a.endsWith('.') ? a : `${a}.`);
    }
    if (openDraft) bits.push('A draft note is open.');
    return bits.join(' ');
  }, [visits, conditions, openDraft]);

  /** The PMH bubble's prose — the same facts the structured timeline expands. */
  const pmh = useMemo(() => {
    const bits: string[] = [];
    for (const c of history?.chronicDiseases ?? []) {
      if (c.status === 'resolved') continue;
      bits.push(c.onsetDate ? `${c.name} ${year(c.onsetDate)}` : c.name);
    }
    for (const s of history?.surgeries ?? []) bits.push(`${s.procedure} ${year(s.date)}`);
    const smoke = patient?.socialHistory?.smokingStatus;
    if (smoke === 'former') bits.push('Ex-smoker');
    else if (smoke === 'current') bits.push('Current smoker');
    /* What was DICTATED into the history (the card's mic) is its free text. */
    const dictated = (history?.notes ?? '').trim();
    const prose = bits.join('. ') + (bits.length ? '.' : '');
    return [prose, dictated].filter(Boolean).join(' ');
  }, [history, patient?.socialHistory?.smokingStatus]);

  /**
   * THE STRUCTURED TIMELINE (f7) — "every line knows where it came from".
   * Each event is a stored row with its own date, so the source tag is a fact
   * about which table it is in, not a label.
   */
  const historyEvents = useMemo(() => {
    const ev: Array<{ id: string; year: string; label: string; icd?: string; source: string; at: number }> = [];
    for (const c of history?.chronicDiseases ?? [])
      ev.push({ id: c.id, year: year(c.onsetDate), label: c.name, icd: c.icdCode, source: `chronic · ${c.status}`, at: dateOf(c.onsetDate) });
    for (const s of history?.surgeries ?? [])
      ev.push({ id: s.id, year: year(s.date), label: s.procedure, source: s.hospital ? `surgery · ${s.hospital}` : 'surgery', at: dateOf(s.date) });
    for (const p of history?.pastConditions ?? [])
      ev.push({ id: p.id, year: p.yearDiagnosed ? String(p.yearDiagnosed) : '—', label: p.condition, source: p.yearResolved ? `resolved ${p.yearResolved}` : 'past condition', at: p.yearDiagnosed ? Date.UTC(p.yearDiagnosed, 0) : 0 });
    for (const i of history?.immunizations ?? [])
      ev.push({ id: i.id, year: year(i.date), label: i.vaccine, source: 'immunisation', at: dateOf(i.date) });
    ev.sort((a, b) => b.at - a.at);
    return ev;
  }, [history]);

  const activeProblemCount = conditions.length;

  /**
   * VITALS AS MEASURES, NOT VISITS (f5c). The endpoint returns one row per
   * visit with every measure on it; the screen wants one row per measure with
   * every reading under it, so the series is transposed here.
   */
  const measures = useMemo<Measure[]>(() => {
    const rows = ((vitalsQ.data ?? []) as Array<Record<string, unknown>>).filter(Boolean);
    if (!rows.length) return [];
    const asc = [...rows].sort((a, b) => dateOf(a.date as string) - dateOf(b.date as string));
    /* `flagField` names the column the server judged. Blood pressure is two
       columns and one reading: the worse of the two verdicts is the reading's,
       because a systolic of 190 over a normal diastolic is still a crisis. */
    const worst = (r: Record<string, unknown>, fields: string[]): VitalFlag | undefined => {
      const flags = (r.flags ?? {}) as Record<string, VitalFlag>;
      const rank: Record<string, number> = { normal: 0, abnormal: 1, critical: 2 };
      const found = fields.map((f) => flags[f]).filter(Boolean) as VitalFlag[];
      if (!found.length) return undefined;
      return found.sort((a, b) => rank[b] - rank[a])[0];
    };

    const defs: Array<{
      key: string; label: string;
      read: (r: Record<string, unknown>) => string | null;
      flag: (r: Record<string, unknown>) => VitalFlag | undefined;
    }> = [
      { key: 'bp', label: 'Blood pressure', read: (r) => (r.bloodPressureSystolic && r.bloodPressureDiastolic ? `${r.bloodPressureSystolic}/${r.bloodPressureDiastolic}` : null), flag: (r) => worst(r, ['bloodPressureSystolic', 'bloodPressureDiastolic']) },
      /* A PULSE IS A BARE NUMBER. The row is already called "Heart rate" and
         the board prints "76": the unit repeats the label on every line. It
         survives where it is part of how the figure is read — "82 kg", "98%",
         "36.4 °C" — and nowhere else. */
      { key: 'hr', label: 'Heart rate', read: (r) => (r.heartRate ? `${r.heartRate}` : null), flag: (r) => worst(r, ['heartRate']) },
      { key: 'temp', label: 'Temperature', read: (r) => (r.temperature ? `${r.temperature} °C` : null), flag: (r) => worst(r, ['temperature']) },
      { key: 'spo2', label: 'Oxygen saturation', read: (r) => (r.oxygenSaturation ? `${r.oxygenSaturation}%` : null), flag: (r) => worst(r, ['oxygenSaturation']) },
      { key: 'rr', label: 'Respiratory rate', read: (r) => (r.respiratoryRate ? `${r.respiratoryRate} /min` : null), flag: (r) => worst(r, ['respiratoryRate']) },
      /* Weight has no reviewed band — a weight is not normal or abnormal
         without a height, an age and a reason. It stays a number. */
      { key: 'wt', label: 'Weight', read: (r) => (r.weight ? `${r.weight} kg` : null), flag: () => undefined },
    ];
    return defs
      .map<Measure | null>((d) => {
        const readings = asc
          .map((r): VitalReading | null => {
            const value = d.read(r);
            return value
              ? {
                  value,
                  at: r.date as string,
                  by: (r.takenBy as string | null) ?? null,
                  flag: d.flag(r),
                }
              : null;
          })
          .filter((x): x is VitalReading => x !== null);
        if (!readings.length) return null;
        return {
          key: d.key,
          label: d.label,
          latest: readings[readings.length - 1].value,
          readings: [...readings].reverse(),
          first: readings[0],
        };
      })
      .filter((m): m is Measure => !!m);
  }, [vitalsQ.data]);

  /**
   * PREGNANCY IS A SAFETY FLAG, and it expires. "Pregnant" with an expected
   * date in the past is a stale row, not a fact about today, so it stops being
   * drawn — the chart says what is true now or says nothing.
   */
  const pregnancy = useMemo(() => {
    const status = patient?.pregnancyStatus ?? null;
    if (status !== 'pregnant') return null;
    const due = patient?.pregnancyDueDate ?? null;
    /* `date` columns arrive as YYYY-MM-DD strings — compare as strings. */
    const today = new Date().toISOString().slice(0, 10);
    if (due && due < today) return null;
    return {
      label: 'Pregnant',
      full: due ? `Pregnant — expected ${due}` : 'Pregnant — no expected date recorded',
    };
  }, [patient?.pregnancyStatus, patient?.pregnancyDueDate]);

  const vitalsPermitted = !forbidden(vitalsQ.error);

  /**
   * MEDS CLUSTER BY PRESCRIPTION, not by drug (f6's "Triple therapy ×3").
   * `prescriptionGroupId` is what the signing flow writes for scripts issued
   * together; the record and then the date are fallbacks for older rows.
   */
  const meds = useMemo(() => {
    if (forbidden(rxQ.error)) return { groups: [] as MedGroup[], active: [] as Prescription[], permitted: false };
    const rx = (rxQ.data ?? []) as Prescription[];
    const by = new Map<string, Prescription[]>();
    for (const p of rx) {
      const key = p.prescriptionGroupId || p.medicalRecordId || String(p.writtenDate ?? p.createdAt).slice(0, 10);
      const arr = by.get(key);
      if (arr) arr.push(p);
      else by.set(key, [p]);
    }
    const groups: MedGroup[] = [...by.entries()]
      .map(([id, drugs]) => {
        const date = drugs
          .map((d) => d.writtenDate ?? d.createdAt)
          .sort((a, b) => dateOf(b) - dateOf(a))[0];
        /* A STATUS NEVER LAPSES — a 10-day course written in May is still
           "signed" in September. Current means the status AND the course
           window (utils/prescriptionStatus). */
        const active = drugs.some((d) => isCurrentlyTaking(d));
        const lead = drugs[0]?.medicationName ?? 'Prescription';
        return {
          id,
          title: drugs.length > 1 ? `${lead} +${drugs.length - 1}` : lead,
          drugs,
          date: date ? String(date) : undefined,
          active,
        };
      })
      .sort((a, b) => dateOf(b.date) - dateOf(a.date));
    /* ACTIVE NOW is what the patient takes today — each drug once, the
       newest script of it (a repeat of the same Cetirizine is one line). */
    const seen = new Set<string>();
    const active = rx
      .filter((p) => isCurrentlyTaking(p))
      .sort((a, b) => dateOf(b.writtenDate ?? b.createdAt) - dateOf(a.writtenDate ?? a.createdAt))
      .filter((p) => {
        const k = [p.medicationGenericName || p.medicationName, p.strength].filter(Boolean).join('|').toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    return { groups, active, permitted: true };
  }, [rxQ.data, rxQ.error]);

  return {
    isLoading,
    isError,
    failed: bundle.failed,
    patient,
    id: patientId,
    name,
    ageSex,
    initials: (name.match(/\b\w/g) ?? []).slice(0, 2).join('').toUpperCase(),
    quickNote: typeof patient?.notes === 'string' ? patient.notes : null,
    allergies,
    allergiesDeclaredNone,
    pregnancy,
    conditions,
    summary,
    pmh,
    /** The stored history row the PMH mic writes into (null: none yet). */
    historyId: history?.id ?? null,
    historyNotes: history?.notes ?? '',
    historyEvents,
    activeProblemCount,
    episodes,
    openEpisodes,
    episode,
    episodesLoading: episodesQ.isLoading,
    visits,
    visitsPermitted,
    visitsTotal: visitsQ.total,
    visitsHasMore: visitsQ.hasMore,
    loadMoreVisits: visitsQ.loadMore,
    loadingMoreVisits: visitsQ.isLoadingMore,
    openDraft,
    labs,
    imaging,
    attachments,
    referrals,
    measures,
    vitalsPermitted,
    meds,
    go: navigate,
  };
}

export type DoctorPatientFile = ReturnType<typeof useDoctorPatientFile>;
